export const formatDateIE = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

/**
 * Formats a Date object into a short, relative time string.
 * - Less than 1 minute: "Just now"
 * - Less than 1 hour: "Xm ago"
 * - Less than 1 day: Time (e.g., "3:30 PM")
 * - Less than 7 days: Short Day Name (e.g., "Wed")
 * - Otherwise: Short Date (e.g., "Jan 1, 2026")
 */
export const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
  const diffInDays = Math.floor(diffInMinutes / (60 * 24));

  // If the date is invalid or in the future (unlikely for a timestamp)
  if (isNaN(date.getTime()) || date.getTime() > now.getTime()) {
      return 'Future'; 
  }

  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  
  // Same day check (only show time)
  if (diffInDays < 1) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }

  // Same week check (show short day)
  if (diffInDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
  }
  
  // Everything else (show short date)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const formatEuroCurrency = (amount?: number): string => {
  if (amount === undefined || amount === null) return '—';
  return `€${Number(amount).toLocaleString('en-IE')}`;
};
// Shared formatting utilities

export const formatCurrency = (
  amount: number,
  locale: string = 'en-IE',
  currency: string = 'EUR'
): string => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency
  }).format(amount);
};

export const getMonthName = (month: number): string => {
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];
  return months[month - 1] || 'Unknown';
};

export const getOrdinalSuffix = (day: number): string => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};
